// The JQL the builder writes, shown as it will be sent, with "Edit JQL" to take over the text.
// Back to the builder only while the text is still something the builder can show.
import { PencilSimple, SlidersHorizontal } from "@phosphor-icons/react";
import { Textarea } from "rizzui";

import { useT } from "../../../i18n";
import type { Draft } from "../draft";
import { fromJql, toJql } from "../jql";

const KEYWORD = /^(AND|OR|NOT|IN|IS|EMPTY|ORDER|BY|ASC|DESC)$/i;

/** Keywords, quoted values and functions in their own colours on the dark panel. */
function Highlighted({ jql }: { jql: string }) {
  const parts = jql.match(/"(?:[^"\\]|\\.)*"|\w+\(\)|[\w-]+|[^\w"]+/g) ?? [];
  return (
    <>
      {parts.map((p, i) => {
        const tone = p.startsWith('"') ? "text-orange" : p.endsWith("()") ? "text-blue" : KEYWORD.test(p) ? "font-semibold text-primary" : undefined;
        return tone ? (
          <span key={i} className={tone}>
            {p}
          </span>
        ) : (
          p
        );
      })}
    </>
  );
}

export function JqlBox({ jira, onChange }: { jira: Draft["jira"]; onChange: (next: Draft["jira"]) => void }) {
  const t = useT().tasks.create.jira;
  const parsed = jira.raw ? fromJql(jira.text) : null;
  const jql = toJql(jira.query);
  return (
    <section aria-label={t.jql} className="min-w-0">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-gray-900">{t.jql}</h3>
        {jira.raw ? (
          <button
            type="button"
            disabled={parsed === null}
            onClick={() => parsed && onChange({ ...jira, raw: false, query: parsed })}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary enabled:hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden /> {t.useBuilder}
          </button>
        ) : (
          <button type="button" onClick={() => onChange({ ...jira, raw: true, text: jql })} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary hover:underline">
            <PencilSimple className="h-4 w-4" aria-hidden /> {t.editJql}
          </button>
        )}
      </div>
      {jira.raw ? (
        <>
          <Textarea
            aria-label={t.jql}
            value={jira.text}
            rows={4}
            textareaClassName="font-mono text-[12.5px] [font-variant-ligatures:none]"
            onChange={(e) => onChange({ ...jira, text: e.target.value })}
          />
          <p className="mb-0 mt-1.5 text-[13px] text-gray-500">{parsed === null && jira.text.trim() !== "" ? t.cannotBuild : t.rawHint}</p>
        </>
      ) : (
        <pre className="m-0 min-h-[3.25rem] whitespace-pre-wrap break-words rounded-lg border border-gray-1000/10 bg-panel dark:border-gray-300 px-3.5 py-3 font-mono text-[12.5px] leading-relaxed text-panel-foreground [font-variant-ligatures:none]" data-testid="jql">
          {jql ? <Highlighted jql={jql} /> : <span className="text-gray-400">{t.emptyJql}</span>}
        </pre>
      )}
    </section>
  );
}
